import { useEffect, useRef, useState } from 'react';
import { VegaButton, VegaButtonLink, VegaFlex, VegaFont, VegaInput } from '@globalpayments/vega-react';
import { Navigate, useLocation } from 'react-router';
import { isEmpty } from 'lodash';

import { Pages } from '../../types/pages';
import { failure } from '../../types/api';

import { useAuth } from '../../utility/auth';
import { getQueryParams } from '../../utility/helper';
import PageSkeleton from '../../components/pageSkeleton';
import authStyles from '../auth.module.css';

const Start = () => {
  const auth = useAuth();
  const location: any = useLocation();
  const codeInputRef = useRef<HTMLVegaInputElement>(null);
  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<boolean>(false);

  const [failed, setFailed] = useState<string>('');

  const [starting, setStarting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleStart = (linkCode: string) => {
    if (linkCode.trim().length > 0 && linkCode.length < 200 && !isEmpty(linkCode)) {
      setCode(linkCode);
      setLoading(true);
    } else setError(true);
  };

  useEffect(() => {
    const params: { code?: string } = getQueryParams();
    if (params?.code != null) {
      handleStart(params.code);
    }
  }, []);

  useEffect(() => {
    const startSession = async () => {
      const data = await auth.login(code);
      setFailed('');
      if (data === failure) {
        setFailed('Failed to start session.');
      }

      setStarting(false);
      setLoading(false);
    };

    if (loading && !starting) {
      setStarting(true);
      startSession();
    }
  }, [loading, auth, code, starting]);

  useEffect(() => {
    void codeInputRef.current?.doFocus();
  }, []);

  const sent = location.state?.sent ?? false;
  const from = location.state?.from?.pathname || '/';

  const onPaste = (e: any) => {
    handleStart(e.clipboardData.getData('text/plain'));
  };

  if (auth.user != null) return <Navigate to={from} />;
  else if (loading) return <PageSkeleton label="Starting session" variant="form" />;
  return (
    <VegaFlex alignItems="center" className={authStyles.form} direction="col" gap="size-16" justifyContent="center">
      {sent && (
        <VegaFont as="p" variant="font-p2-short">
          If your account exists in our records, we sent a login code.
        </VegaFont>
      )}
      <VegaInput
        autocomplete="off"
        autoValidation={false}
        className={authStyles.input}
        id="Code"
        isValid={!error}
        label="Code"
        maxLength={200}
        onKeyDown={(event) => {
          if (event.code === 'Enter') {
            handleStart(code);
          }
        }}
        onPaste={onPaste}
        onVegaChange={(event) => setCode(event.detail)}
        ref={codeInputRef}
        value={code}
        valueUpdateTrigger="input"
      />
      {error && (
        <VegaFont as="p" color="text-danger-link" variant="font-footnote1-short">
          Invalid code.
        </VegaFont>
      )}
      <VegaButton label="Start" onVegaClick={() => handleStart(code)} />
      {failed.length > 0 && (
        <VegaFont as="p" color="text-danger-link" variant="font-p2-short">
          {failed}
        </VegaFont>
      )}
      <VegaButtonLink label="Need a login code?" link={Pages.Login} />
    </VegaFlex>
  );
};

export default Start;
